'use client';

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { getMarketingScrollRoot } from './marketing-scroll';
import { MARKETING } from './marketing-tokens';

const FROM_TRANSFORM = {
  up: 'translateY(20px)',
  down: 'translateY(-32px)',
  left: 'translateX(-48px)',
  right: 'translateX(48px)',
};

const REDUCED_MOTION_SX = {
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    transition: 'none',
  },
};

export default function MarketingReveal({
  children,
  sx,
  component = 'div',
  id,
  observe = true,
  animate = true,
  from = 'up',
  delay = 0,
  duration = 0.85,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!observe) return undefined;

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.dataset.revealed = '1';
          }
        });
      },
      {
        root: getMarketingScrollRoot(node),
        rootMargin: '0px 0px -6% 0px',
        threshold: 0.06,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [observe]);

  const motionSx = animate
    ? {
        opacity: 0,
        transform: FROM_TRANSFORM[from],
        transition: `opacity ${duration}s ${MARKETING.ease} ${delay}s, transform ${duration}s ${MARKETING.ease} ${delay}s`,
        ...(observe
          ? {
              '&[data-revealed="1"]': {
                opacity: 1,
                transform: 'none',
              },
            }
          : {
              '[data-revealed="1"] &': {
                opacity: 1,
                transform: 'none',
              },
            }),
        ...REDUCED_MOTION_SX,
      }
    : {};

  return (
    <Box
      ref={ref}
      component={component}
      id={id}
      {...(observe ? { 'data-reveal': true } : {})}
      sx={{
        ...motionSx,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

MarketingReveal.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  component: PropTypes.elementType,
  id: PropTypes.string,
  observe: PropTypes.bool,
  animate: PropTypes.bool,
  from: PropTypes.oneOf(['up', 'down', 'left', 'right']),
  delay: PropTypes.number,
  duration: PropTypes.number,
};
